import * as Phaser from "phaser";
import {MoveCommand, computeFacingCommand} from "../model/messages";
import {CookSprite} from "./cook_sprite";
import {StationSprite} from "./station_sprite";
import {IngredientSprite} from "./ingredient_sprite";
import {BasketSprite} from "./basket_sprite";

export function isTouchDevice(): boolean {
    return 'ontouchstart' in window && navigator.maxTouchPoints > 0;
}

export interface TouchActionCallbacks {
    onPick: () => void;
    onChopStart: (stationId: string) => void;
    onChopStop: () => void;
    onStartRound: () => void;
    onFace: (direction: MoveCommand) => void;
}

const TAP_THRESHOLD = 15; // pixels: max movement to count as a tap vs drag
const CHOP_HOLD_MS = 200; // ms hold before triggering chop on a cutting board station
const FACING_DELAY_MS = 32; // ms between facing command and action

const STOP_COMMAND: MoveCommand = { left: false, right: false, up: false, down: false };

export class TouchInputHandler {
    private _scene: Phaser.Scene;
    private _callbacks: TouchActionCallbacks;
    private _getLocalCook: () => CookSprite | undefined;

    private _startX: number = 0;
    private _startY: number = 0;
    private _downObject: Phaser.GameObjects.GameObject | null = null;
    private _chopTimer: Phaser.Time.TimerEvent | null = null;
    private _isChopping: boolean = false;

    constructor(
        scene: Phaser.Scene,
        callbacks: TouchActionCallbacks,
        getLocalCook: () => CookSprite | undefined
    ) {
        this._scene = scene;
        this._callbacks = callbacks;
        this._getLocalCook = getLocalCook;
        this._registerListeners();
    }

    private _registerListeners(): void {
        this._scene.input.on('gameobjectdown', this._onGameObjectDown, this);
        this._scene.input.on('gameobjectup', this._onGameObjectUp, this);
        // Floor tap (drop while carrying) and start round on lobby overlay
        this._scene.input.on('pointerup', this._onPointerUp, this);
    }

    private _onGameObjectDown(
        pointer: Phaser.Input.Pointer,
        gameObject: Phaser.GameObjects.GameObject
    ): void {
        this._startX = pointer.x;
        this._startY = pointer.y;
        this._downObject = gameObject;
        this._isChopping = false;

        if (gameObject instanceof StationSprite) {
            const station = gameObject.instance;
            // Schedule chop after hold threshold if station could be choppable
            this._chopTimer = this._scene.time.delayedCall(CHOP_HOLD_MS, () => {
                const cook = this._getLocalCook();
                if (!cook) return;
                this._faceTarget(cook, gameObject.x, gameObject.y, () => {
                    this._callbacks.onChopStart(station.id);
                    this._isChopping = true;
                });
            });
        }
    }

    private _onGameObjectUp(
        pointer: Phaser.Input.Pointer,
        gameObject: Phaser.GameObjects.GameObject
    ): void {
        if (this._isChopping) {
            this._cancelChopTimer();
            this._callbacks.onChopStop();
            this._isChopping = false;
            this._downObject = null;
            return;
        }

        this._cancelChopTimer();

        // Only act if this is the same object pressed (tap)
        if (gameObject !== this._downObject) {
            this._downObject = null;
            return;
        }

        const dx = pointer.x - this._startX;
        const dy = pointer.y - this._startY;
        const moved = Math.sqrt(dx * dx + dy * dy);

        if (moved >= TAP_THRESHOLD) {
            this._downObject = null;
            return;
        }

        this._handleTap(gameObject);
        this._downObject = null;
    }

    private _onPointerUp(pointer: Phaser.Input.Pointer): void {
        // If a game object handled this, _downObject was cleared there
        if (this._downObject !== null) return;

        // Floor tap: if cook is carrying, trigger pick (which acts as drop on server)
        const dx = pointer.x - this._startX;
        const dy = pointer.y - this._startY;
        const moved = Math.sqrt(dx * dx + dy * dy);
        if (moved < TAP_THRESHOLD) {
            const cook = this._getLocalCook();
            if (cook?.instance?.carries_id) {
                this._callbacks.onPick();
            }
        }
    }

    private _handleTap(gameObject: Phaser.GameObjects.GameObject): void {
        const cook = this._getLocalCook();
        if (!cook) return;

        let targetX: number;
        let targetY: number;

        if (gameObject instanceof StationSprite) {
            targetX = gameObject.x;
            targetY = gameObject.y;
            this._faceTarget(cook, targetX, targetY, () => {
                this._callbacks.onPick();
            });
        } else if (gameObject instanceof IngredientSprite) {
            targetX = gameObject.x;
            targetY = gameObject.y;
            this._faceTarget(cook, targetX, targetY, () => {
                this._callbacks.onPick();
            });
        } else if (gameObject instanceof BasketSprite) {
            targetX = gameObject.x;
            targetY = gameObject.y;
            this._faceTarget(cook, targetX, targetY, () => {
                this._callbacks.onPick();
            });
        } else if (gameObject instanceof CookSprite) {
            targetX = gameObject.x;
            targetY = gameObject.y;
            this._faceTarget(cook, targetX, targetY, () => {
                this._callbacks.onPick();
            });
        }
    }

    private _faceTarget(cook: CookSprite, targetX: number, targetY: number, action: () => void): void {
        const faceCmd = computeFacingCommand(cook.x, cook.y, targetX, targetY);
        this._callbacks.onFace(faceCmd);

        this._scene.time.delayedCall(FACING_DELAY_MS, () => {
            action();
            this._scene.time.delayedCall(FACING_DELAY_MS, () => {
                this._callbacks.onFace(STOP_COMMAND);
            });
        });
    }

    private _cancelChopTimer(): void {
        if (this._chopTimer) {
            this._chopTimer.destroy();
            this._chopTimer = null;
        }
    }
}
